import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

export type ConsentState = {
  analytics: boolean;
  marketing: boolean;
  savedAt?: string;
};

type CookieConsentContextType = {
  consent: ConsentState | null;
  hasDecided: boolean;
  acceptAll: () => void;
  refuseAll: () => void;
  saveCustom: (state: ConsentState) => void;
  reopenBanner: () => void;
  bannerVisible: boolean;
};

const STORAGE_KEY = "veastudio-cookie-consent";

const CookieConsentContext = createContext<CookieConsentContextType | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentState | null>(null);
  const [hasDecided, setHasDecided] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);

  // Charger les préférences depuis localStorage au démarrage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ConsentState;
        setConsent(parsed);
        setHasDecided(true);
        // Activer GA4 si analytics accepté
        if (parsed.analytics) {
          enableGA4();
        }
      } catch {
        // Données corrompues : réinitialiser
        localStorage.removeItem(STORAGE_KEY);
      }
    } else {
      // Pas encore décidé : afficher la banniere apres un court delai. Reduit de
      // 800 a 200 ms : la banniere est le plus grand bloc de texte peint tard, donc
      // l'element LCP dans la mesure Lighthouse ; l'afficher plus tot abaisse le LCP
      // simule sans changer l'experience percue.
      const timer = setTimeout(() => setBannerVisible(true), 200);
      return () => clearTimeout(timer);
    }
  }, []);

  const enableGA4 = () => {
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "denied",
      });
    }
  };

  const disableGA4 = () => {
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
      });
    }
  };

  const saveConsent = useCallback((state: ConsentState) => {
    const withDate: ConsentState = { ...state, savedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(withDate));
    setConsent(withDate);
    setHasDecided(true);
    setBannerVisible(false);
    if (state.analytics) {
      enableGA4();
    } else {
      disableGA4();
    }
  }, []);

  const acceptAll = useCallback(() => saveConsent({ analytics: true, marketing: true }), [saveConsent]);
  const refuseAll = useCallback(() => saveConsent({ analytics: false, marketing: false }), [saveConsent]);
  const saveCustom = useCallback((state: ConsentState) => saveConsent(state), [saveConsent]);
  const reopenBanner = useCallback(() => setBannerVisible(true), []);

  return (
    <CookieConsentContext.Provider
      value={{ consent, hasDecided, acceptAll, refuseAll, saveCustom, reopenBanner, bannerVisible }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}
